// Vocabulary (audit glossary §1, designs/08-audits-and-process/2026-06-22-frontend-navigation-...-audit.md):
// this registry defines the **client routes** (a.k.a. thin-shell routes) — routes the frontend mounts
// locally with NO HTML document fetch, reusing the already-mounted **chrome** and synthesizing the
// `<head>` state by hand (see ClientRouteDocumentState + HeadMetaManifest). Distinct from ordinary
// server-document soft navigation, which fetches the destination HTML and swaps it in.
import { MetaHandler } from "@/main/common/MetaHandler";
import type { ClientRouteDescriptor, ClientRouteMatch } from "@/main/navigation/ClientRouteDescriptor";
import {
    DASHBOARD_SECTIONS,
    DASHBOARD_SUBSECTIONS,
} from "@/main/navigation/dashboardSectionVocabulary";
import { normalizeSearchType } from "@/main/react/page/search/types";
import { resolveSettingsInitialSection } from "@/main/react/settings/SettingsSections";
import {
    ALL_NOTIFICATIONS_SCOPE,
    normalizeNotificationFeedScope,
} from "@/main/react/notifications/notificationFeedScope";
import { normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import {
    DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
    EDGE_NAVIGATION_ROOT_CLASS_NAME,
    EDITOR_NAVIGATION_ROOT_CLASS_NAME,
    EDITOR_VIEWPORT_CONTRACT,
    MESSAGES_MOUNT_CLASS_NAME,
    MESSAGES_NAVIGATION_ROOT_CLASS_NAME,
    MESSAGES_VIEWPORT_CONTRACT,
} from "@/main/navigation/NavigationRootShell";
import { LocalizationUtil } from "@/main/util/LocalizationUtil";
// No imports of its own, so this costs the eager graph nothing (rule 27).
import {
    homeFeedTypeSupportsSort,
    homeOrderingSupportsRange,
    normalizeScopedHomeOrderingStyle,
    type HomeOrderingStyle,
    type HomeRange,
} from "@/main/react/page/home-page/HomePageShared";
// Declared in `ClientRouteDocumentState`, beside the indexable default it overrides, so a route and
// an island that corrects a route's verdict after a fetch share ONE spelling (FS-5, 2026-09-20).
import { NO_INDEX_FOLLOW, userProfileCanonicalUrl } from "@/main/navigation/ClientRouteDocumentState";
import { buildSearchRouteDocumentState } from "@/main/navigation/searchRouteDocumentState";
// No imports of its own (a types-and-constants module), so this costs the eager graph nothing (rule 27).
import { isConversationFilter } from "@/main/common/ConversationsTypes";

function toAbsoluteUrl(path: string): string {
    return new URL(path, window.location.origin).href;
}

function decodePathSegment(value: string | undefined): string {
    if (!value) {
        return "";
    }

    try {
        return decodeURIComponent(value).trim();
    } catch {
        return value.trim();
    }
}

type ClientHomeFeedType = "home" | "all" | "groups" | "following" | "series" | "unanswered" | "picks";

function normalizeHomeFeedType(value: string | null | undefined, isLoggedIn: boolean): ClientHomeFeedType {
    if (value === "all") {
        return "all";
    }

    if (value === "home") {
        return isLoggedIn ? "home" : "all";
    }

    // The three personal scopes (audience-scopes design §9): members-only, and an anonymous link
    // degrades to `all` exactly as `HomePage.normalizeFeedType` and the server do. Without these the
    // canonical URL this route emits dropped the `feed` param and a soft navigation to a scope link
    // landed on the default feed.
    if (value === "groups" || value === "following" || value === "series") {
        return isLoggedIn ? value : "all";
    }

    // The TRANSIENT lane (audience-scopes design §7.4 + §9 note, FI-022): `unanswered` renders for
    // everyone (its endpoint is allowlisted and its row set does not depend on the viewer). `boards`
    // was the second, retired round 61 D12 (2026-10-03): it now falls to the default below. This is
    // the third copy of `HomeController.normalizeFeedType`'s ladder (`HomePage.normalizeFeedType` is
    // the second); until 2026-09-14 it stopped at the five persisted feeds, so a soft navigation to a
    // lane mounted the default feed and rewrote the address, while a document load rendered the lane.
    if (value === "unanswered") {
        return "unanswered";
    }

    // SV-189 slice b (FI-4782): the Editor's picks lane, transient and open to everyone like `unanswered`.
    if (value === "picks") {
        return "picks";
    }

    return isLoggedIn ? "home" : "all";
}

/**
 * The server's vocabulary, exactly (`HomeFeedOrderingStyle.fromStringOrDefault`): the four named sorts,
 * else `active`. Until FS-1 (2026-09-15) this registry typed the ordering as `top | active | new` and
 * collapsed `agreed`, `controversial` and `arguments` onto its own "top", which `isIndexableHomeState`
 * then read as the indexable bare feed while the server had shipped `noindex,follow`, and which
 * `HomePage.normalizeOrderingStyle` reads as the RETIRED name for `agreed` when it arrives as the
 * dataset value. Legacy `top` collapses onto `active` here as it does on the server.
 */
function normalizeHomeOrdering(value: string | null | undefined): HomeOrderingStyle {
    if (value === "new" || value === "agreed" || value === "controversial" || value === "arguments") {
        return value;
    }

    return "active";
}

function normalizeHomeRange(value: string | null | undefined): HomeRange {
    if (value === "day" || value === "week" || value === "month" || value === "year") {
        return value;
    }

    return "all";
}

/**
 * The client half of `SeoService.buildForHome`'s indexability rule: the all-feed in its default
 * ordering (`active`) over all time, and nothing else. An explicit range is read as a slice even on a
 * sort the server ignores it for (`/?feed=all&range=week` is canonical `/` there): that edge is a URL
 * nobody links, and the safe direction is noindex (gate `848db59a7e`, pinned in the robots spec).
 */
function isIndexableHomeState(feedType: ClientHomeFeedType, ordering: HomeOrderingStyle, range: HomeRange): boolean {
    return feedType === "all" && ordering === "active" && range === "all";
}

function buildCanonicalHomeUrl(feedType: ClientHomeFeedType, ordering: HomeOrderingStyle, range: HomeRange, isLoggedIn: boolean): URL {
    const params = new URLSearchParams();
    const defaultFeedType = isLoggedIn ? "home" : "all";

    if (feedType !== defaultFeedType) {
        params.set("feed", feedType);
    }

    // FI-2813: every feed with a sort axis (`all` and the three sortable scopes) carries its non-default sort and, for
    // a count sort, its range, exactly as `HomePage.buildHomePageUrl` writes them.
    if (homeFeedTypeSupportsSort(feedType) && ordering !== "active") {
        params.set("ordering", ordering);
    }

    if (homeFeedTypeSupportsSort(feedType) && homeOrderingSupportsRange(ordering) && range !== "all") {
        params.set("range", range);
    }

    const search = params.toString();
    return new URL(`/${search ? `?${search}` : ""}`, window.location.origin);
}

function resolveHomeRoute(url: URL): ClientRouteMatch | null {
    const isLoggedIn = MetaHandler.isUserLoggedIn();
    const hasExplicitHomeQueryState = url.searchParams.has("feed")
        || url.searchParams.has("ordering")
        || url.searchParams.has("range");

    if (isLoggedIn && url.pathname === "/" && !hasExplicitHomeQueryState) {
        return null;
    }

    const legacyFeedType = url.pathname === "/all"
        ? "all"
        : url.pathname === "/home"
            ? "home"
            : null;
    const feedType = normalizeHomeFeedType(url.searchParams.get("feed") ?? legacyFeedType, isLoggedIn);
    // FI-2813: the three sortable scopes keep their sort, normalized to the three a scope serves
    // (`HomePage.normalizeHomePageState`), and their range only on a count sort. Forcing `active` over all time here
    // rewrote a Back to `/?feed=following&ordering=new` into `/?feed=following`. `home` and the lane have no sort.
    const isAllFeed = feedType === "all";
    const ordering: HomeOrderingStyle = isAllFeed
        ? normalizeHomeOrdering(url.searchParams.get("ordering"))
        : homeFeedTypeSupportsSort(feedType)
            ? normalizeScopedHomeOrderingStyle(normalizeHomeOrdering(url.searchParams.get("ordering")))
            : "active";
    const range: HomeRange = isAllFeed || (homeFeedTypeSupportsSort(feedType) && homeOrderingSupportsRange(ordering))
        ? normalizeHomeRange(url.searchParams.get("range"))
        : "all";
    const canonicalUrl = buildCanonicalHomeUrl(feedType, ordering, range, isLoggedIn);

    return {
        routeId: "home",
        url: canonicalUrl,
        componentName: "HomePage",
        mountClassName: "react-component w-full flex justify-center",
        navigationRootClassName: EDGE_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        // No sibling containers: round 100 D1/D2/A3 (SV-155 1b, 2026-10-13) retired the two islands `home.jte`
        // used to emit ahead of the page mount (FI-047's pending-decision row and checklist). They are notices
        // inside HomePage's feed now, and nothing may mount above the home tabs (D3; guarded by
        // `EnhancedNavigation.spec.ts` and `HomeTemplateNoContainerBeforeHomePageTests`). A client-owned
        // arrival carries no `data-checklist-closed`, which the checklist reads as "not told" and fetches.
        dataset: {
            feedType,
            ordering,
            range,
        },
        documentState: {
            // Mirror the SSR <title> for "/" (backend messages.properties `home.title`) so the
            // client soft-nav head doesn't regress to the bare site name. See enhanced-navigation
            // smoke coverage + designs/…/frontend-navigation audit (client head must mirror SSR).
            title: LocalizationUtil.get("home.page-title"),
            canonicalUrl: canonicalUrl.href,
            // The server's rule (SeoService.buildForHome, 2026-07-26): ONLY the bare "/" is indexable;
            // every query variant is noindex,follow — a personal scope, AND an ordering or range slice of
            // the all-feed (their membership churns with the top-score jitter, so they must never enter
            // the index as near-duplicates of "/"). Mirror the whole rule, not the feed half of it: until
            // gate 848db59a7e (2026-09-14) this read `feedType !== "all"`, so a soft navigation or a
            // Back to `/?feed=all&ordering=new` re-wrote the head the server had shipped as noindex into
            // the rich indexable directive. Pinned in ClientRouteRegistry.homeRobots.spec.ts.
            robots: isIndexableHomeState(feedType, ordering, range) ? null : NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

function resolveGroupsDirectoryRoute(url: URL): ClientRouteMatch {
    const query = (url.searchParams.get("q") ?? "").trim();
    const tag = (url.searchParams.get("tag") ?? "").trim();
    const canonicalParams = new URLSearchParams();
    if (query) {
        canonicalParams.set("q", query);
    } else if (tag) {
        // q and tag are mutually exclusive modes; a free-text query wins.
        canonicalParams.set("tag", tag);
    }
    const canonicalSearch = canonicalParams.toString();
    const canonicalUrl = new URL(`/groups${canonicalSearch ? `?${canonicalSearch}` : ""}`, window.location.origin);

    return {
        routeId: "groupsDirectory",
        url: canonicalUrl,
        componentName: "GroupsDirectoryPage",
        mountClassName: "react-component w-full flex justify-center groups-directory-react",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            query: encodeURIComponent(query),
            tag: encodeURIComponent(query ? "" : tag),
        },
        documentState: {
            title: LocalizationUtil.get("groups.directory.title"),
            canonicalUrl: toAbsoluteUrl(`/groups${!query && tag ? `?tag=${encodeURIComponent(tag)}` : ""}`),
            // Search states are noindex (mirrors /search); the bare directory and tag pages are indexable.
            robots: query ? NO_INDEX_FOLLOW : null,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

function resolveSearchRoute(url: URL): ClientRouteMatch {
    const query = url.searchParams.get("searchQuery") ?? "";
    const type = normalizeSearchType(url.searchParams.get("searchType"), "discussions");
    const canonicalUrl = new URL(url.pathname + url.search, window.location.origin);

    return {
        routeId: "search",
        url: canonicalUrl,
        componentName: "SearchResultsPage",
        mountClassName: "react-component w-full flex justify-center search-results-react",
        navigationRootClassName: EDGE_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            searchQuery: encodeURIComponent(query),
            searchType: type,
        },
        // Frontend review F9: the one `/search` head rule, shared with `SearchResultsPage` (it trims the query in the
        // title, as the server does; this route used to print it untrimmed).
        documentState: buildSearchRouteDocumentState(
            query,
            canonicalUrl.href,
            (key, ...args) => LocalizationUtil.get(key, ...args),
        ),
        shellStatePatch: {
            searchQuery: query,
            searchType: type,
        },
    };
}

function resolveTagRoute(url: URL): ClientRouteMatch | null {
    const parts = url.pathname.split("/").filter(Boolean);
    const rawTag = decodePathSegment(parts[1]).replace(/^#+/, "").trim();
    if (!rawTag) {
        return null;
    }

    const indexableUrl = new URL(`/tag/${encodeURIComponent(rawTag)}`, window.location.origin);

    // ⚠️ `?languageScope=all` is ADDRESS state, and the address is what the member gets back.
    //
    // `TagController` reads it as a `@RequestParam`, and `TagPage`'s `useHistoryBackedState` reads it
    // from `location.search` before it falls back to the dataset — so a canonical URL that dropped it
    // would rewrite the address AND silently reset the list to "my languages" on the way in. That was
    // invisible while `/tag/{tag}` was hard-navigated (the server saw the real query string); it became
    // reachable the moment the route joined `SAFE_ROUTE_PATTERNS` on 2026-09-01, via a Back onto a
    // scope-bearing entry whose page instance is gone.
    //
    // ⚠️ Only the one RECOGNISED value is carried — deliberately not `url.search` wholesale, which is
    // what `resolveSearchRoute` does. There the query string IS the route's state; here an unknown
    // param has no reader on either side, and echoing it back would mint addresses the server never
    // produces. Adding a second `@RequestParam` to `TagController` obliges a second line here.
    const addressUrl = new URL(indexableUrl.href);
    const requestedLanguageScope = url.searchParams.get("languageScope") === "all" ? "all" : "mine";
    if (requestedLanguageScope === "all") {
        addressUrl.searchParams.set("languageScope", "all");
    }

    const heading = `#${rawTag}`;

    return {
        routeId: "tag",
        url: addressUrl,
        componentName: "TagPage",
        mountClassName: "react-component w-full flex justify-center tag-react",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            tag: rawTag,
            // The island's fallback when `location.search` carries nothing. Kept in step with the
            // address above so the two readers cannot disagree.
            languageScope: requestedLanguageScope,
        },
        documentState: {
            // Tag pages are indexable (unlike /search): omit the robots override so the synthesized
            // <head> keeps the default indexable directive.
            title: heading,
            // The BARE path, deliberately — not `addressUrl`. `SeoService.buildForTag` derives the
            // server's canonical from the tag name alone, so a language-scoped view is the same
            // indexable document as the unscoped one and must not advertise a second canonical.
            canonicalUrl: indexableUrl.href,
            openGraphType: "website",
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

/**
 * `/s/{slug}` — a REVIEW-facet subject page.
 *
 * <p>The slug is opaque here and is passed through untouched: it embeds the subject's public id, and
 * normalizing it (the way the tag resolver strips `#`) would break the one thing it has to preserve.
 *
 * <p>Only the id-bearing props are carried in `dataset`. The name and the aggregate are NOT: on a
 * soft navigation there is no server render to read them from, so putting stale values here would let
 * the island paint one subject's rating under another subject's heading for a frame. The island
 * fetches what it needs and the heading comes with it.
 */
function resolveSubjectRoute(url: URL): ClientRouteMatch | null {
    const parts = url.pathname.split("/").filter(Boolean);
    const slug = decodePathSegment(parts[1]).trim();
    if (!slug) {
        return null;
    }

    const canonicalUrl = new URL(`/s/${encodeURIComponent(slug)}`, window.location.origin);

    return {
        routeId: "subject",
        url: canonicalUrl,
        componentName: "SubjectPage",
        // Must byte-match the class attribute on subject.jte's react-component container.
        mountClassName: "react-component w-full flex justify-center subject-react",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            subjectSlug: slug,
        },
        documentState: {
            // NOT indexable by default (FS-5, 2026-09-20). The server's rule is
            // `SeoService.buildForSubject` ending `build(structuredData(...), !hasAnyReview)`: a subject
            // with ZERO reviews is served `noindex`. This descriptor carried no `robots` key until
            // 2026-09-20, and `ClientRouteDocumentState.ts:39` reads `state.robots ?? INDEXABLE_ROBOTS`
            // — so from the moment `/s/{slug}` was re-admitted to soft navigation (`fbed5a4063`,
            // 2026-09-16) a soft hop or a Back onto a review-less subject published the rich indexable
            // directive over a page the server had marked noindex. Rule 32 inverted.
            //
            // The route resolver cannot know `ratingCount` — the summary has not been fetched yet — so
            // it takes the conservative direction, and `SubjectPage` widens it to indexable once the
            // summary says there is at least one review. Wrong-but-noindex costs a crawl; wrong-but-
            // indexable puts a thin page in the index.
            robots: NO_INDEX_FOLLOW,
            title: slug,
            canonicalUrl: canonicalUrl.href,
            openGraphType: "website",
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

function resolveMessagesRoute(url: URL): ClientRouteMatch {
    const canonicalUrl = new URL(url.pathname + url.search, window.location.origin);

    return {
        routeId: "messages",
        url: canonicalUrl,
        componentName: "MessagesPage",
        mountClassName: MESSAGES_MOUNT_CLASS_NAME,
        navigationRootClassName: MESSAGES_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: MESSAGES_VIEWPORT_CONTRACT,
        dataset: {
            targetUsername: url.searchParams.get("targetUsername") ?? "",
            threadPublicId: url.searchParams.get("threadPublicId") ?? "",
            focusComposer: url.searchParams.get("focusComposer") === "true" ? "true" : "false",
        },
        documentState: {
            title: LocalizationUtil.get("messages.page-title"),
            canonicalUrl: toAbsoluteUrl("/messages"),
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

// A copy of UserPage.tsx's `USER_PAGE_SECTIONS` (round 12 `2e` added `images`, `2a` the missing `participated`;
// round 31 `3a` added `replies`, SV-147 A8 removed it); `ClientRouteRegistry.userSections.spec.ts` reads the page's
// list from source and fails when the two drift.
const USER_PAGE_SECTIONS = ["discussions", "images", "participated", "arguments", "followers", "following", "groups", "series"] as const;
const USER_GROUP_SECTIONS = ["leader", "member"] as const;
/** A8 (FI-2853): the profile query state `UserPage` reads back from the address (`readState`). */
const USER_PAGE_ADDRESS_PARAMS = ["ordering", "view", "entry", "as"] as const;

function normalizeUserPageSection(section?: string | null): (typeof USER_PAGE_SECTIONS)[number] {
    return USER_PAGE_SECTIONS.includes((section ?? "") as (typeof USER_PAGE_SECTIONS)[number])
        ? (section as (typeof USER_PAGE_SECTIONS)[number])
        : "discussions";
}

function normalizeUserGroupSection(section?: string | null): (typeof USER_GROUP_SECTIONS)[number] {
    return USER_GROUP_SECTIONS.includes((section ?? "") as (typeof USER_GROUP_SECTIONS)[number])
        ? (section as (typeof USER_GROUP_SECTIONS)[number])
        : "leader";
}

function resolveUserRoute(url: URL): ClientRouteMatch | null {
    const parts = url.pathname.split("/").filter(Boolean);
    const rawUsername = decodePathSegment(parts[1]);
    const username = normalizeDisplayUsername(rawUsername) ?? rawUsername;
    if (!username) {
        return null;
    }

    const section = normalizeUserPageSection(decodePathSegment(parts[2]));
    const groupSection = section === "groups"
        ? normalizeUserGroupSection(decodePathSegment(parts[3]))
        : "leader";
    // A8 (FI-2853): a bare `/user/{name}` is a different address from `/user/{name}/discussions`. The server serves a
    // signed-in member the wall there (`UserController.renderUserPage`: `defaultMainColumnView` is "wall" only when the
    // viewer is logged in AND the URL named no section), so the address stays bare and the island gets the same
    // default through `dataset.defaultView`, the client twin of `data-default-view`.
    const pathNamesASection = parts.length > 2;
    const canonicalPath = pathNamesASection
        ? `/user/${encodeURIComponent(username)}/${encodeURIComponent(section)}${section === "groups" ? `/${encodeURIComponent(groupSection)}` : ""}`
        : `/user/${encodeURIComponent(username)}`;
    const canonicalUrl = new URL(canonicalPath, window.location.origin);
    // The params `UserPage.buildUserPageUrl` writes, verbatim (the page normalizes them); every other param is dropped.
    for (const name of USER_PAGE_ADDRESS_PARAMS) {
        const value = url.searchParams.get(name);
        if (value !== null && value !== "") {
            canonicalUrl.searchParams.set(name, value);
        }
    }
    const defaultView = !pathNamesASection && MetaHandler.isUserLoggedIn() ? "wall" : "sections";

    return {
        routeId: "user",
        url: canonicalUrl,
        componentName: "UserPage",
        mountClassName: "react-component w-full flex justify-center user-react",
        navigationRootClassName: EDGE_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            username,
            section,
            defaultView,
            hasInitialBootstrap: "false",
        },
        // FI-2091: `url` above is the tab ADDRESS; the declared canonical is the parent profile, as the server says.
        documentState: {
            title: username || LocalizationUtil.get("literal.deleted_user"),
            // FI-2571: a deep discussions page keeps the server's cursor self-canonical.
            canonicalUrl: userProfileCanonicalUrl(username, window.location.origin, { section, search: url.search }),
            openGraphType: "profile",
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

// The dashboard's section vocabulary is imported, NOT re-declared. It used to be hand-copied here as
// "the soft-navigation copy", with a comment asking the next person to keep it in step, and it had
// already drifted: `arguments` was missing `deleted`, so a soft navigation to
// `/my_dashboard/arguments/deleted` fell through `normalizeDashboardSection2` to `available[0]` and
// mounted the AUTHORED list at an address the app then rewrote to agree with itself. Deduplicated
// 2026-08-31 (`FQ-F4`); see `dashboardSectionVocabulary.ts` for the whole account.
//
// ⚠️ `normalizeDashboardSection` still falls back to `discussions` for an unrecognised section, and that
// fallback is deliberate — surviving `/my_dashboard/your-turn` links must land on a page rather than a
// 404. It is also why an omission here is silent, which is what makes the single source load-bearing.

function normalizeDashboardSection(section?: string | null): (typeof DASHBOARD_SECTIONS)[number] {
    return DASHBOARD_SECTIONS.includes((section ?? "") as (typeof DASHBOARD_SECTIONS)[number])
        ? (section as (typeof DASHBOARD_SECTIONS)[number])
        : "discussions";
}

function normalizeDashboardSection2(section1: (typeof DASHBOARD_SECTIONS)[number], section2?: string | null): string {
    const available = DASHBOARD_SUBSECTIONS[section1];
    if (available.length === 0) {
        return "";
    }

    return available.includes((section2 ?? "") as string)
        ? (section2 as string)
        : available[0];
}

function resolveDashboardMetricsRoute(url: URL): ClientRouteMatch | null {
    const username = MetaHandler.getLoggedInUsername();
    if (!username) {
        return null;
    }

    const canonicalUrl = new URL("/my_dashboard/metrics", window.location.origin);

    return {
        routeId: "dashboard",
        url,
        componentName: "UserMetricsPage",
        mountClassName: "react-component w-full flex justify-center my-dashboard-metrics-react",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            username,
            initialPeriodType: "day",
            initialPeriods: "7",
        },
        documentState: {
            title: LocalizationUtil.get("typescript.userMetrics.title"),
            canonicalUrl: canonicalUrl.href,
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

/** FI-2813: the dashboard state `MyDashboardPage` keeps in the query (`buildDashboardUrl`); nothing else is carried. */
const DASHBOARD_ROUTE_STATE_PARAMS = ["ordering", "query", "facet"] as const;

function resolveDashboardRoute(url: URL): ClientRouteMatch | null {
    const username = MetaHandler.getLoggedInUsername();
    if (!username) {
        return null;
    }

    const parts = url.pathname.split("/").filter(Boolean);
    const section1 = normalizeDashboardSection(parts[1]);
    const section2 = normalizeDashboardSection2(section1, parts[2]);
    const canonicalPath = `/my_dashboard/${encodeURIComponent(section1)}${section2 ? `/${encodeURIComponent(section2)}` : ""}`;
    const canonicalUrl = new URL(canonicalPath, window.location.origin);
    // FI-2813: the ADDRESS keeps the params `MyDashboardPage.buildDashboardUrl` writes (the page normalizes them), so a
    // Back to a sorted, searched or faceted list re-mounts that list; the declared canonical stays the bare path.
    const routeParams = new URLSearchParams();
    for (const name of DASHBOARD_ROUTE_STATE_PARAMS) {
        const value = url.searchParams.get(name);
        if (value !== null && value !== "") {
            routeParams.set(name, value);
        }
    }
    const routeSearch = routeParams.toString();
    const routeUrl = routeSearch ? new URL(`${canonicalPath}?${routeSearch}`, window.location.origin) : canonicalUrl;

    return {
        routeId: "dashboard",
        url: routeUrl,
        componentName: "MyDashboardPage",
        mountClassName: "react-component w-full flex justify-center my-dashboard-react",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            section1,
            section2,
            username,
            // Also the cue to fetch the recap offer (FI-1157).
            hasInitialBootstrap: "false",
            userCreationDate: "",
            userDescription: "",
            userProfilePictureUrl: MetaHandler.getLoggedInProfilePictureUrl() ?? "",
        },
        documentState: {
            title: LocalizationUtil.get("my_dashboard.title", username),
            canonicalUrl: canonicalUrl.href,
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

function resolveSettingsRoute(url: URL): ClientRouteMatch | null {
    const username = MetaHandler.getLoggedInUsername();
    if (!username) {
        return null;
    }

    const parts = url.pathname.split("/").filter(Boolean);
    const requestedSection = decodePathSegment(parts[1]) || url.searchParams.get("initialSection") || undefined;
    const initialSection = resolveSettingsInitialSection(requestedSection);
    const canonicalUrl = new URL(`/settings/${encodeURIComponent(initialSection)}`, window.location.origin);

    return {
        routeId: "settings",
        url: canonicalUrl,
        componentName: "Settings",
        mountClassName: "react-component",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            initialSection,
            hasInitialBootstrap: "false",
            username,
        },
        documentState: {
            title: LocalizationUtil.get("settings.title"),
            canonicalUrl: canonicalUrl.href,
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

/**
 * `/notifications` and `/notifications/{scope}` — the addressable notifications page.
 *
 * <p><b>Members only, and the null return is the enforcement of that here.</b> There is no anonymous
 * reading of somebody's inbox: a signed-out visitor gets no client route at all, so the navigation
 * falls through to a real document fetch and the server's authenticated posture answers. Same shape as
 * `resolveSettingsRoute` directly above.
 *
 * <p><b>The canonical URL keeps the form the member is on.</b> `/notifications` and
 * `/notifications/all` are the same page, and rewriting one to the other would move a member off an
 * address they typed or bookmarked for no benefit — see `pathnameForScope` in `NotificationsPage.tsx`,
 * whose reasoning this mirrors so the soft and hard paths agree about the address.
 *
 * <p>`noindex,follow`, matching `NotificationsPageController`. A client route sets the document state
 * itself, so a route that forgot this would leave the previous page's robots directive in place on a
 * private inbox.
 */
function resolveNotificationsRoute(url: URL): ClientRouteMatch | null {
    const username = MetaHandler.getLoggedInUsername();
    if (!username) {
        return null;
    }

    const parts = url.pathname.split("/").filter(Boolean);
    const requestedScope = decodePathSegment(parts[1]);
    const initialScope = normalizeNotificationFeedScope(requestedScope || undefined);
    const canonicalPathname = initialScope === ALL_NOTIFICATIONS_SCOPE && parts.length < 2
        ? "/notifications"
        : `/notifications/${encodeURIComponent(initialScope)}`;

    const canonicalUrl = new URL(canonicalPathname, window.location.origin);
    const initialFilter = url.searchParams.get("filter") === "unread" ? "unread" : "all";
    if (initialFilter === "unread") {
        canonicalUrl.searchParams.set("filter", "unread");
    }

    return {
        routeId: "notifications",
        url: canonicalUrl,
        componentName: "NotificationsPage",
        mountClassName: "react-component w-full",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {
            initialScope,
            initialFilter,
        },
        documentState: {
            title: LocalizationUtil.get("notifications.page.title"),
            canonicalUrl: canonicalUrl.href,
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

/**
 * `/conversations` — the member's second phone tab (SV-169, SV-180 D2). U43 / FI-3675 (r156, ⚑G-20 + ⚑G-35): it was
 * the one bottom-bar destination with no descriptor, so a tap and its Back were full document loads that lost the
 * member's scroll. Same shape as `resolveNotificationsRoute` above: members only (the null return sends a visitor to
 * the real document, whose controller redirects), `noindex,follow` as `conversations.jte` declares.
 *
 * <p><b>The address keeps `?filter=`</b> (rule 33): `ConversationsPage` holds its tab in `useHistoryBackedState`, and a
 * descriptor whose url dropped the query would undo that on every Back onto a filtered entry (FE#QD-7). An unknown
 * value is dropped, as the page's own `readFilter` drops it. The page reads no dataset; on a client route the
 * server's `<h1>` is absent, so the island draws its own heading (`hasServerRenderedPageHeading`).
 */
function resolveConversationsRoute(url: URL): ClientRouteMatch | null {
    const username = MetaHandler.getLoggedInUsername();
    if (!username) {
        return null;
    }

    const routeUrl = new URL("/conversations", window.location.origin);
    const filter = url.searchParams.get("filter");
    if (isConversationFilter(filter) && filter !== "all") {
        routeUrl.searchParams.set("filter", filter);
    }

    return {
        routeId: "conversations",
        url: routeUrl,
        componentName: "ConversationsPage",
        mountClassName: "react-component w-full",
        navigationRootClassName: DEFAULT_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: null,
        dataset: {},
        documentState: {
            // The frontend catalog has no `conversations.page.title`; the island's own heading key is reused.
            title: LocalizationUtil.get("header.tooltip.conversations"),
            canonicalUrl: new URL("/conversations", window.location.origin).href,
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

function resolveCreateDiscussionRoute(url: URL): ClientRouteMatch | null {
    const username = MetaHandler.getLoggedInUsername();
    if (!username) {
        return null;
    }

    const parts = url.pathname.split("/").filter(Boolean);
    const rawDraftIdentifier = parts[2] ?? "";
    const draftLoadMode = rawDraftIdentifier === "new"
        ? "forceCreateNew"
        : rawDraftIdentifier
            ? "byPublicId"
            : "reuseEmptyOrCreate";
    const draftPublicId = draftLoadMode === "byPublicId"
        ? decodeURIComponent(rawDraftIdentifier)
        : "";
    const canonicalUrl = new URL(url.pathname + url.search, window.location.origin);
    const releaseProbeUrl = draftLoadMode === "byPublicId"
        ? canonicalUrl
        : new URL("/create/discussion/release-probe", window.location.origin);

    return {
        routeId: "createDiscussion",
        url: canonicalUrl,
        releaseProbeUrl,
        mountBeforeReleaseProbe: draftLoadMode !== "byPublicId",
        componentName: "CreateDiscussionPage",
        mountClassName: "react-component w-full",
        navigationRootClassName: EDITOR_NAVIGATION_ROOT_CLASS_NAME,
        viewportContract: EDITOR_VIEWPORT_CONTRACT,
        dataset: {
            currentUsername: username,
            draftLoadMode,
            draftPublicId,
        },
        documentState: {
            title: LocalizationUtil.get("create.create-discussion.page-title"),
            canonicalUrl: canonicalUrl.href,
            robots: NO_INDEX_FOLLOW,
        },
        shellStatePatch: {
            searchQuery: "",
            searchType: "discussions",
        },
    };
}

/**
 * Exported for the soft-eligibility invariant in `NavigationPolicy.softEligibility.spec.ts`, which
 * pins the rule this registry silently depended on: a route with a descriptor here is dead code
 * unless `NavigationPolicy`'s `SAFE_ROUTE_PATTERNS` also admits it. Four routes had drifted out of
 * that agreement before anyone noticed (MAF-7, 2026-08-31), and the failure is silent in the worst
 * way — the route works, it just costs a full document load and loses the member's scroll on Back.
 */
export const CLIENT_ROUTE_DESCRIPTORS: ClientRouteDescriptor[] = [
    {
        routeId: "home",
        match: (url) => url.pathname === "/" || url.pathname === "/home" || url.pathname === "/all",
        resolve: resolveHomeRoute,
    },
    {
        routeId: "groupsDirectory",
        match: (url) => url.pathname === "/groups",
        resolve: resolveGroupsDirectoryRoute,
    },
    {
        routeId: "search",
        match: (url) => url.pathname === "/search",
        resolve: resolveSearchRoute,
    },
    {
        // Single-segment tag pages only; /tag/{tag}/rss is a server RSS endpoint, not a client route.
        routeId: "tag",
        match: (url) => /^\/tag\/[^/]+$/.test(url.pathname),
        resolve: resolveTagRoute,
    },
    {
        // Single-segment subject pages only. /s/ has no sub-routes today, and matching greedily would
        // claim any future /s/{slug}/... the server adds.
        routeId: "subject",
        match: (url) => /^\/s\/[^/]+$/.test(url.pathname),
        resolve: resolveSubjectRoute,
    },
    {
        routeId: "messages",
        match: (url) => url.pathname === "/messages",
        resolve: resolveMessagesRoute,
    },
    {
        routeId: "dashboard",
        match: (url) => url.pathname === "/my_dashboard/metrics",
        resolve: resolveDashboardMetricsRoute,
    },
    {
        routeId: "dashboard",
        match: (url) => url.pathname === "/my_dashboard" || url.pathname.startsWith("/my_dashboard/"),
        resolve: resolveDashboardRoute,
    },
    {
        routeId: "user",
        match: (url) => /^\/user\/[^/]+(?:\/[^/]+(?:\/[^/]+)?)?$/.test(url.pathname),
        resolve: resolveUserRoute,
    },
    {
        routeId: "settings",
        match: (url) => url.pathname === "/settings" || /^\/settings\/[^/]+$/.test(url.pathname),
        resolve: resolveSettingsRoute,
    },
    {
        // `/notifications` and `/notifications/{scope}`. Single-segment only: the page has no
        // sub-routes today, and matching greedily would claim any the server adds later.
        routeId: "notifications",
        match: (url) => url.pathname === "/notifications" || /^\/notifications\/[^/]+$/.test(url.pathname),
        resolve: resolveNotificationsRoute,
    },
    {
        // U43 / FI-3675 (r156): exact path only, like `/messages`; the page has no sub-routes.
        routeId: "conversations",
        match: (url) => url.pathname === "/conversations",
        resolve: resolveConversationsRoute,
    },
    {
        routeId: "createDiscussion",
        match: (url) => url.pathname === "/create/discussion"
            || url.pathname === "/create/discussion/new"
            || /^\/create\/discussion\/[^/]+$/.test(url.pathname),
        resolve: resolveCreateDiscussionRoute,
    },
];

export function resolveClientRoute(url: URL): ClientRouteMatch | null {
    const descriptor = CLIENT_ROUTE_DESCRIPTORS.find((candidate) => candidate.match(url));
    return descriptor ? descriptor.resolve(url) : null;
}
